import {useMutation} from '@apollo/client'
import React, {useEffect} from 'react'
import {Alert, Button, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {IResDeleteStage} from '../../../types/pipelines'
import {DELETE_STAGE_PIPELINE} from '../../../gql/mutations/pipelineMutations'

interface IModalDeleteStage {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit: {id: string; name: string}
}

const quote = '"'

const dsM = defineMessages({
  title: {id: 'leads.stages.deleteTitle', defaultMessage: 'Delete Stage'},
  confirm: {id: 'leads.stages.deleteConfirm', defaultMessage: 'Are you sure you want to delete this stage <b>{name}</b>?'},
  warnNoLead: {id: 'leads.stages.warnNoLead', defaultMessage: 'This stage will be eliminated if there is no associated lead'},
  toastRemoved: {id: 'leads.stages.toast.removed', defaultMessage: 'Stage removed successfully'},
})

export const ModalDeleteStage: React.FC<IModalDeleteStage> = ({
  isOpen,
  onClose,
  refetch,
  infoEdit,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const [gqlAddEditStage, {data, loading, error}] =
    useMutation<IResDeleteStage>(DELETE_STAGE_PIPELINE)

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.deleteStageMutation.success) {
      toast.success(intl.formatMessage(dsM.toastRemoved))
      onClose()
      refetch()
    } else if (data.deleteStageMutation?.errors?.length > 0) {
      toast.error(errMsg(data.deleteStageMutation?.errors?.[0]?.message || intl.formatMessage({id: 'leads.add.toast.somethingHappened', defaultMessage: 'something happened'})))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDelete = () => {
    gqlAddEditStage({
      variables: {
        stageId: parseId(infoEdit.id),
      },
    })
  }
  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(dsM.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h2 className='text-danger pb-5'>
          {intl.formatMessage(dsM.confirm, {name: infoEdit.name, b: (chunks: (string | JSX.Element)[]) => <span className='text-primary'>{quote}{chunks}{quote}</span>})}
        </h2>
        <Alert variant='warning' className='mt-6 d-flex align-items-center gap-3'>
          <i className='bi bi-exclamation-triangle fs-1' style={{color: 'inherit'}} /> {intl.formatMessage(dsM.warnNoLead)}
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button type='button' variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          variant='danger'
          className='d-flex align-items-center'
          disabled={loading}
          onClick={handleDelete}
        >
          {loading && <Spinner animation='border' size='sm' />}
          <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
